// external imports
import React, { useEffect, useState, useContext, useRef } from 'react';
import { Row, Col } from 'reactstrap';
import Select from 'react-select';
import Skeleton from 'react-loading-skeleton';
import { Tooltip } from 'react-tooltip';

// local imports
import formatDate from '../../helpers/formatDate';
import exportConfig from '../../config';

// custom hooks & context
import { pipelineContext } from '../../contexts/pipelineContext';
import { endpointContext } from '../../contexts/endpointContext';

const QCFormUpload = ({
	handleSubmit,
	formValue,
	setFormValue,
	formValid,
	setFormValid,
	loading,
	fetchSites,
	fileInputRef,
	handlePostQCDataFileUpload,
	handleUploadFileClick,
	setUploadedPostQCDataFile,
	uploadedPostQCDataFile
}) => {
	const [todayDate, setTodayDate] = useState(() => {
		const today = new Date();
		const year = today.getFullYear();
		const month = String(today.getMonth() + 1).padStart(2, '0');
		const day = String(today.getDate()).padStart(2, '0');
		return `${year}-${month}-${day}`;
	});

	const prevCommoditiesRef = useRef(formValue.formCommodities);

	const [siteOptions, setSiteOptions] = useState([]);
	const [selectedCommForSites, setSelectedCommForSites] = useState(null);
	const [selectedOptions, setSelectedOptions] = useState([]);
	const pipelineContextQCFormUpload = useContext(pipelineContext);
	const endpointContextQCFormUpload = useContext(endpointContext);

	const [chooseUploadFile, setChooseUploadFile] = useState(false);

	let now = new Date().toLocaleTimeString('en-GB', {
		hour: '2-digit',
		minute: '2-digit',
		hour12: false
	});

	useEffect(() => {
		pipelineContextQCFormUpload.setStraightToUpload(true);

		let today = new Date();
		setFormValue({
			...formValue,
			formLookback: 1,
			formEndDate: today.toISOString().substring(0, 10),
			formEndDateNow: today.toLocaleTimeString(),
			formStartDate: new Date(today.setDate(today.getDate() - 1))
				.toISOString()
				.substring(0, 10)
		});
	}, []);

	useEffect(() => {
		// ensure selected site options are kept if advancing/regressing
		setSelectedOptions(
			siteOptions.filter((opt) => formValue.formSiteId.includes(opt.value))
		);
	}, [siteOptions]);

	const handleOnChange = (event, category) => {
		const { value, checked } = event.target;

		const finalValue = isNaN(parseInt(value)) ? value : parseInt(value);

		setFormValue((prevFormValue) => {
			const updatedCategory = checked
				? [...prevFormValue[category], finalValue]
				: prevFormValue[category].filter((item) => item !== finalValue);

			return {
				...prevFormValue,
				[category]: updatedCategory
			};
		});
	};

	useEffect(() => {
		const prev = prevCommoditiesRef.current;
		const curr = formValue.formCommodities;

		if (fetchSites?.data) {
			var options;
			options = fetchSites.data.data
				.filter((site) => site.commodity_id === formValue.formCommodities[0])
				.map(({ id, name }) => ({
					value: id,
					label: `${id} - ${name}`
				}))
				.sort((a, b) => a.label.localeCompare(b.label));

			setSelectedOptions([]);
			setSiteOptions(options);
		}

		if (prev !== curr) {
			setFormValue({
				...formValue,
				formSiteId: [],
				formCommodities: formValue.formCommodities
			});
		}
	}, [fetchSites.data, formValue.formCommodities]);

	useEffect(() => {
		validateForm();
	}, [formValue]);

	const onChangeManuallySelectSites = (e) => {
		const value = e.target.value === 'true';
		let today = new Date();

		if (value) {
			if (formValue.fullHistory) {
				setFormValue({
					...formValue,
					manuallySelectSites: true,
					formStartDate: '2016-01-01'
				});
			} else {
				setFormValue({
					...formValue,
					manuallySelectSites: true,
					formLookback: 1,
					formEndDate: today.toISOString().substring(0, 10),
					formEndDateNow: today.toLocaleTimeString(),
					formStartDate: new Date(today.setDate(today.getDate() - 1))
						.toISOString()
						.substring(0, 10)
				});
			}
		} else {
			setFormValue({
				...formValue,
				formStartDate: new Date(today.setDate(today.getDate() - 1))
					.toISOString()
					.substring(0, 10),
				manuallySelectSites: false,
				formSiteId: []
			});
			setSelectedOptions([]);
		}
	};

	const handleBackButton = (e) => {
		e.preventDefault();
		setFormValue(
			exportConfig.defaultFormValues.filter(
				(formType) => formType.type === 'Upload'
			)[0].formValues
		);

		pipelineContextQCFormUpload.setQCType(undefined);
		pipelineContextQCFormUpload.setStraightToUpload(false);

		pipelineContextQCFormUpload.setProgressBar({
			width: 2 * (100 / 7),
			title: 'QC Type'
		});
	};

	const calcDate = (e) => {
		let eDate, sDate, sDate2;

		if (e.target.name === 'formLookback') {
			eDate = new Date(formValue.formEndDate);
			sDate = eDate.setDate(eDate.getDate() - e.target.value);
			sDate2 = new Date(sDate).toISOString().substring(0, 10);
			setFormValue({
				...formValue,
				formStartDate: sDate2,
				formLookback: e.target.value
			});
		}

		if (e.target.name === 'formEndDate') {
			eDate = new Date(e.target.value);
			sDate = eDate.setDate(eDate.getDate() - formValue.formLookback);
			sDate2 = new Date(sDate).toISOString().substring(0, 10);

			setFormValue({
				...formValue,
				formEndDate: e.target.value,
				formStartDate: sDate2
			});
		}
	};

	const handleChange = (selectedValues) => {
		setSelectedOptions(selectedValues);
		setFormValue({
			...formValue,
			formSiteId: selectedValues.map((value) => value.value)
		});
	};

	const reloadSites = () => {
		fetchSites.makeRequest(
			`/${pipelineContextQCFormUpload.comm}/sites?hasCaptures=true`,
			'GET',
			null,
			'',
			500
		);
	};

	const moveStraightToUpload = (event) => {
		event.preventDefault();

		if (formValue.formStartDate === '') {
			setFormValue({ ...formValue, formStartDate: '2016-01-01' });
		}

		pipelineContextQCFormUpload.setProgressBar({
			width: 6 * (100 / 7),
			title: 'Upload Captures'
		});
	};

	const validateForm = () => {
		if (formValue.manuallySelectSites) {
			if (
				formValue.fullHistory &&
				formValue.formEndDate !== '' &&
				Array.isArray(formValue.formSiteId) &&
				formValue.formSiteId.length > 0 &&
				formValue.formEndDate > formValue.formStartDate
			) {
				setFormValid(true);
			} else if (
				formValue.formEndDate !== '' &&
				formValue.formStartDate !== '' &&
				Array.isArray(formValue.formSiteId) &&
				formValue.formSiteId.length > 0 &&
				formValue.formEndDate > formValue.formStartDate
			) {
				setFormValid(true);
			} else {
				setFormValid(false);
			}
		} else {
			if (
				formValue.formCommodities.length > 0 &&
				formValue.formProdStates.length > 0 &&
				formValue.formSensors.length > 0 &&
				!parseInt(formValue.formLookback).isNaN
			) {
				setFormValid(true);
			} else {
				setFormValid(false);
			}
		}
	};

	return (
		<>
			<hr></hr>
			<form onSubmit={(e) => moveStraightToUpload(e)} className="px-3 mx-3">
				<Row>
					<Col className="d-flex justify-content-start align-items-center">
						<button
							className="btn interactButtonSecondary backButton"
							onClick={handleBackButton}
							style={{ width: '200px' }}
						>
							<i class="fa-solid fa-left-long pe-3"></i>
							Change QC type
						</button>
						<h1 className="ms-5 mt-2">QC</h1>
					</Col>
					<Col></Col>
					<Col className="d-flex flex-column justify-content-center align-items-end">
						{formValid && !loading ? (
							<>
								<button
									type="submit"
									className="btn interactButton mt-2 mx-0"
									style={{ maxHeight: '40px', width: '200px' }}
									data-tooltip-id="move-to-upload-TT"
								>
									Move to Upload <i class="fa-solid fa-right-long ps-2"></i>
								</button>
							</>
						) : (
							<button
								disabled
								className="btn btn-secondary mt-2 mx-0"
								style={{ maxHeight: '40px', width: '200px' }}
								data-tooltip-id="move-to-upload-TT"
							>
								Move to Upload <i class="fa-solid fa-right-long ps-2"></i>
							</button>
						)}
					</Col>
				</Row>

				<Tooltip id="move-to-upload-TT" place="bottom" effect="solid">
					Move straight to upload based on form criteria
				</Tooltip>

				<h4 className="py-3">Fill in the upload criteria</h4>

				{/* {exportConfig.gainsEnvironment === 'dev' && (
					<Row className="mt-5">
						<div className="form-group" style={{ width: '90%' }}>
							<h5 className="card-title">Upload criteria</h5>
							<br />
							
							<div className="form-group">
								<div className="d-flex flex-row align-items-center mb-3">
									<>
										<input
											id="upload-form-choose-criteria-yes"
											type="radio"
											name="upload-form-how-to-upload"
											value="true"
											className="form-check-input"
											checked={formValue.uploadStaticPostQCDataFile === false}
											onChange={() => {
												setFormValue({
													...formValue,
													uploadStaticPostQCDataFile: false
												});
												setUploadedPostQCDataFile(null);
											}}
										></input>
										<label
											for="upload-form-choose-criteria-yes"
											className="form-label ms-2 me-5 my-0"
										>
											Choose sites criteria
										</label>
									</>

									<>
										<input
											id="upload-form-upload-post-qc-data-file"
											type="radio"
											name="upload-form-how-to-upload"
											value="false"
											className="form-check-input"
											checked={formValue.uploadStaticPostQCDataFile === true}
											onChange={() =>
												setFormValue({
													...formValue,
													uploadStaticPostQCDataFile: true
												})
											}
										></input>
										<label
											for="upload-form-upload-post-qc-data-file"
											className="form-label ms-2 me-4 my-0"
										>
											Upload Post QC Data file
										</label>
									</>
								</div>
							</div>
						</div>
					</Row>
				)} */}

				{formValue.uploadStaticPostQCDataFile ? (
					<>
						{' '}
						<button
							type={'button'}
							className="btn interactButton"
							onClick={handleUploadFileClick}
						>
							Upload File
						</button>
						{uploadedPostQCDataFile && <p>{uploadedPostQCDataFile.name}</p>}
						<input
							ref={fileInputRef}
							type="file"
							style={{ display: 'none' }}
							onChange={(e) => setUploadedPostQCDataFile(e.target.files[0])}
						/>
					</>
				) : (
					<>
						<Row className="mt-5">
							<div className="form-group" style={{ width: '90%' }}>
								<h5 className="card-title">Manually choose sites</h5>
								<br />

								<div className="form-group">
									<div className="d-flex flex-row align-items-center mb-3">
										<>
											<input
												id="upload-form-manually-select-sites-yes"
												type="radio"
												name="upload-form-manually-select-sites"
												value="true"
												className="form-check-input"
												checked={formValue.manuallySelectSites === true}
												onChange={onChangeManuallySelectSites}
											></input>
											<label
												for="upload-form-manually-select-sites-yes"
												className="form-label ms-2 me-5 my-0"
											>
												Yes
											</label>
										</>

										<>
											<input
												id="upload-form-manually-select-sites-no"
												type="radio"
												name="upload-form-manually-select-sites"
												value="false"
												className="form-check-input"
												checked={formValue.manuallySelectSites === false}
												onChange={onChangeManuallySelectSites}
											></input>
											<label
												for="upload-form-manually-select-sites-no"
												className="form-label ms-2 me-4 my-0"
											>
												No
											</label>
										</>
									</div>
								</div>
							</div>
						</Row>

						{formValue.manuallySelectSites ? (
							<Row className="mt-2">
								<div className="col-12 col-lg-6 col-xl-5 py-3 py-md-3 px-2 pe-lg-5">
									<div className="form-group" style={{ width: '90%' }}>
										<h5 className="card-title">Site</h5>
										<p className="fst-italic">Select one site</p> <br></br>
										<div className="form-group">
											<div className="d-flex flex-row flex-wrap align-items-center mb-3">
												{endpointContextQCFormUpload.dbData.allCommodities
													.filter(
														(comm) =>
															`${comm.siteTypeStr}s` ===
															pipelineContextQCFormUpload.comm
													)
													.map((comm, idx) => (
														<>
															<input
																id={`id${comm.id}`}
																type="radio"
																name="historic-form-site-comms-filter"
																key={idx}
																value={comm.id}
																className="form-check-input"
																//checked={selectedCommForSites === comm.id}
																//onClick={() => setSelectedCommForSites(comm.id)}
																checked={formValue.formCommodities.includes(
																	comm.id
																)}
																onClick={() =>
																	setFormValue({
																		...formValue,
																		formCommodities: [comm.id]
																	})
																}
															></input>
															<label
																for={`id${comm.id}`}
																className={`form-label ms-1 me-4 my-0 ${
																	formValue.formCommodities.includes(comm.id)
																		? 'fw-bold'
																		: ''
																}`}
															>
																{comm.name}
															</label>
														</>
													))}
											</div>
										</div>
										{fetchSites.loading ? (
											<Skeleton height={35} />
										) : (
											<>
												{formValue.formSiteId ? (
													<Select
														options={siteOptions}
														value={siteOptions.filter((option) =>
															selectedOptions.includes(option)
														)}
														onChange={handleChange}
														isSearchable
														isMulti // Enable multi-select
														placeholder="Search by site name or id"
													/>
												) : (
													<Select
														options={siteOptions}
														value={selectedOptions}
														onChange={handleChange}
														isSearchable
														isMulti // Enable multi-select
														placeholder="Search by site name or id"
													/>
												)}
											</>

											// <>
											// 	<Select
											// 		options={siteOptions}
											// 		value={
											// 			formValue.formSiteId
											// 				? siteOptions.filter((option) =>
											// 						Array.isArray(formValue.formSiteId)
											// 							? formValue.formSiteId.includes(option.value)
											// 							: option.value === formValue.formSiteId
											// 				  )
											// 				: selectedOptions
											// 		}
											// 		onChange={handleChange}
											// 		isSearchable
											// 		isMulti // Enables multi-select
											// 		placeholder="Search by site name or id"
											// 	/>
											// </>

											// <>
											// 	{formValue.formSiteId ? (
											// 		<Select
											// 			options={siteOptions}
											// 			value={
											// 				siteOptions.filter(
											// 					(option) => option.value === formValue.formSiteId
											// 				)[0]
											// 			}
											// 			onChange={handleChange}
											// 			isSearchable
											// 			placeholder="Search by site name or id"
											// 		/>
											// 	) : (
											// 		<Select
											// 			options={siteOptions}
											// 			value={selectedOptions}
											// 			onChange={handleChange}
											// 			isSearchable
											// 			placeholder="Search by site name or id"
											// 		/>
											// 	)}
											// </>
										)}
										{fetchSites.error && (
											<h6 className=" mt-2">
												<i
													id="sentinel-hub-icon"
													onClick={() => reloadSites()}
													className="me-2 fa-solid fa-arrows-rotate"
													data-tooltip-id="reload-sites-historic-form"
												></i>{' '}
												Could not fetch sites at this time
											</h6>
										)}
										{fetchSites.loading && (
											<h6 className="mt-2">Loading sites ..</h6>
										)}
										<Tooltip
											id="reload-sites-historic-form"
											place="bottom"
											effect="solid"
										>
											Reload sites
										</Tooltip>
									</div>
								</div>
								<div className="col-12 col-lg-6 col-xl-5 py-3 py-md-3 px-2 pe-lg-5">
									<div className="form-group">
										<h5 className="card-title">Interval</h5>
										<p className="fst-italic">Set interval period</p> <br></br>
										<div className="row">
											<div className="d-flex flex-column">
												<div className="d-flex flex-row">
													<input
														id="full-history-true"
														type="radio"
														name="QCFormHistoricFullHistory"
														value={true}
														className="form-check-input"
														defaultChecked={formValue.fullHistory === true}
														onClick={(e) =>
															setFormValue({
																...formValue,
																fullHistory: true,
																formStartDate: ''
															})
														}
													></input>
													<label
														for="full-history-true"
														className="form-label ms-2"
													>
														Full capture history
													</label>
												</div>
												<div className="d-flex flex-row">
													<input
														id="full-history-false"
														type="radio"
														name="QCFormHistoricFullHistory"
														value={false}
														defaultChecked={formValue.fullHistory === false}
														className="form-check-input"
														onClick={(e) =>
															setFormValue({
																...formValue,
																fullHistory: false
															})
														}
													></input>
													<label
														for="full-history-false"
														className="form-label ms-2"
													>
														Choose specific dates
													</label>
												</div>
											</div>
										</div>
										<div className="row mt-5">
											<h6>Selecting captures between</h6>

											<div className="col-5 d-flex justify-content-evenly">
												{formValue.formStartDate ? (
													<span className="fw-bold">
														{formatDate(formValue.formStartDate)}
													</span>
												) : (
													<span className="fw-bold">2016-01-01</span>
												)}

												<p> at </p>
												<span className="fw-bold ms-2"> 00:00</span>
											</div>
											<div className="col-2 d-flex justify-content-center">
												and{' '}
											</div>
											<div className="col-5 d-flex justify-content-evenly">
												<span className="fw-bold">
													{formatDate(formValue.formEndDate)}
												</span>{' '}
												at {now}
											</div>
										</div>
										{formValue.fullHistory === false && (
											<Row className="py-2">
												<Col className="col-6 pe-1">
													<label for="start_date">Start date</label> <br></br>
													{formValue.fullHistory ? (
														<input
															className="form-control text-sm"
															id="start_date"
															name="formStartDate"
															title="start date of interval"
															max={new Date().toISOString().split('T')[0]}
															type="date"
															value=""
															disabled
														></input>
													) : (
														<input
															className="form-control text-sm"
															id="start_date"
															name="formStartDate"
															title="start date of interval"
															type="date"
															max={new Date().toISOString().split('T')[0]}
															value={formValue.formStartDate}
															onChange={(e) =>
																setFormValue({
																	...formValue,
																	formStartDate: e.target.value
																})
															}
														></input>
													)}
													<br></br>
												</Col>
												<Col className="col-6 ps-1">
													<label for="end_date">End date</label> <br></br>
													<input
														className="form-control text-sm"
														id="end_date"
														name="formEndDate"
														title="End date of interval"
														type="date"
														max={new Date().toISOString().split('T')[0]}
														value={formValue.formEndDate}
														onChange={(e) =>
															setFormValue({
																...formValue,
																formEndDate: e.target.value
															})
														}
													></input>
													<br></br>
												</Col>

												{formValue.formStartDate > formValue.formEndDate && (
													<p id="sensor-err" className="text-warning pt-3">
														<i class="fa-solid fa-triangle-exclamation"></i> The
														start date is after the end date
													</p>
												)}
											</Row>
										)}
									</div>
								</div>
							</Row>
						) : (
							<Row className="mt-2">
								<Col className="col-lg-3 col-6">
									<div className="form-group">
										<h5 className="card-title">Commodity</h5>
										<p className="fst-italic">Select one</p>

										<br></br>
										{endpointContextQCFormUpload.dbData.allCommodities
											.filter(
												(comm) =>
													`${comm.siteTypeStr}s` ===
													pipelineContextQCFormUpload.comm
											)
											.map((comm, idx) => (
												<>
													<input
														className="form-check-input form-commodity"
														key={idx}
														type="radio"
														id={comm.id}
														name="daily-qc-comms"
														value={comm.id}
														onClick={(e) =>
															setFormValue({
																...formValue,
																formCommodities: [comm.id]
															})
														}
														checked={formValue.formCommodities.includes(
															comm.id
														)}
													></input>
													<label className="px-2" for={comm.id}>
														{comm.name}
													</label>
													<br></br>
												</>
											))}
									</div>
									<br></br>

									{formValue.formCommodities.length === 0 && (
										<p id="comm-err" className="text-warning">
											<i class="fa-solid fa-triangle-exclamation"></i> Select at
											least one commodity
										</p>
									)}
								</Col>

								<Col className="col-lg-4 col-12">
									<h5 className="card-title">Production State</h5>
									<p className="fst-italic">Select at least one</p>

									<br></br>
									{exportConfig.production_states.map(
										({ state, descript, formText }, idx) => (
											<>
												<input
													className="form-check-input form-prod-state"
													type="checkbox"
													id={state}
													name="daily-qc-prods"
													value={state}
													checked={formValue.formProdStates.includes(state)}
													onChange={(e) => handleOnChange(e, 'formProdStates')}
												></input>
												<label
													className="px-2"
													for={state}
													data-tooltip-id={`prodStatToolTip${state}`}
												>
													{`${state} - ${formText}`}
												</label>{' '}
												<Tooltip
													id={`prodStatToolTip${state}`}
													place="top"
													effect="solid"
												>
													{descript}
												</Tooltip>
												<br></br>
											</>
										)
									)}
									{formValue.formProdStates.length === 0 && (
										<p id="prod-state-err" className="text-warning pt-3">
											<i class="fa-solid fa-triangle-exclamation"></i> Select at
											least one production state
										</p>
									)}
								</Col>
								<Col className="col-lg-4 col-12">
									<div className="form-group">
										<h5 className="card-title">Interval</h5>
										<p className="fst-italic">
											Adjust lookback days or end data to adjust interval
										</p>{' '}
										<br></br>
										<label for="num-lookback-days">
											Days lookback: <strong>{formValue.formLookback}</strong>
										</label>
										<input
											type="range"
											className="form-range"
											id="lookback-range"
											name="formLookback"
											min={1}
											max={10}
											step={1}
											value={parseInt(formValue.formLookback)}
											onChange={(e) => calcDate(e)}
										></input>
										<Row className="my-3">
											<Col className="col-6 pe-1">
												<label for="start_date">Start date</label> <br></br>
												<input
													className="form-control text-sm"
													id="start_date"
													name="start_date"
													title="start date of interval"
													type="date"
													value={formValue.formStartDate}
													disabled
												></input>
												<br></br>
											</Col>
											<Col className="col-6 ps-1">
												<label for="end_date">End date</label>
												<br></br>
												<input
													className="form-control text-sm"
													id="end_date"
													name="formEndDate"
													title="End date of interval"
													type="date"
													max={todayDate}
													value={formValue.formEndDate}
													onChange={(e) => calcDate(e)}
												></input>
												<br></br>
											</Col>
										</Row>
										<div className="row">
											<h6>Selecting captures between</h6>

											<div className="col-5 d-flex justify-content-evenly">
												<span className="fw-bold">
													{formatDate(formValue.formStartDate)}
												</span>
												<p> at </p>
												<span className="fw-bold"> 00:00</span>
											</div>
											<div className="col-2 d-flex justify-content-center">
												and{' '}
											</div>
											<div className="col-5 d-flex justify-content-evenly">
												<span className="fw-bold">
													{formatDate(formValue.formEndDate)}
												</span>{' '}
												at <span className="fw-bold">{now}</span>
											</div>
										</div>
									</div>
								</Col>
							</Row>
						)}
					</>
				)}
			</form>
		</>
	);
};

export default QCFormUpload;
